import React, { useEffect, useState } from 'react';
import { animateScroll as scroll } from 'react-scroll/modules';

import ValutaFilter from '@eika-infrastruktur/ui-valuta-filter';
import { LinkButton, StandardButton } from '@eika/button';
import { Checkbox } from '@eika/checkbox';
import { IconTypes } from '@eika/icon';
import { Link } from '@eika/link';
import { ButtonToggles } from '@eika/radio-button';

import { FormTypeCompletionDetails, UpdateConsent } from '../../../actions';
import { ButtonGroupStringChangeEvent, DispatchWithState, StringKeyValue } from '../../../actions/types';
import InfoBox from '../../../components/Messages/InfoBox';
import { CalculatorReducer } from '../../../reducers/calculator';
import { CompletionReducer } from '../../../reducers/completion';
import { FeatureToggleReducer } from '../../../reducers/feature';
import { TrackingStep, trackEvent } from '../../../util/piwik-utils';

interface Props {
    action: {
        loanCompletionDetailsNextEvent: (form: FormTypeCompletionDetails) => DispatchWithState;
        updateConsent: (name: UpdateConsent['name'], value: UpdateConsent['value']) => void;
        openStep: (step: number) => void;
        fieldUpdateInsurance: ButtonGroupStringChangeEvent;
    };
    completion: CompletionReducer;
    calculator: CalculatorReducer;
    feature: FeatureToggleReducer;
    text: StringKeyValue;
}

const LoanCompletionInsurance = (props: Props): JSX.Element => {
    const { action, completion, text, calculator, feature } = props;

    const { fieldUpdateInsurance } = action;
    const { ongoing } = completion;
    const { loanInfo } = calculator;
    const [insuranceConfirmed, setInsuranceConfirmed] = useState(false);
    const [hasErrors, setHasErrors] = useState(false);

    const JA = 'Ja';
    const NEI = 'Nei';
    const NEXT_BUTTON = 'Neste';
    const PREV_BUTTON = 'Tilbake';

    const prevStep = () => {
        action.openStep(1);
    };

    useEffect(() => {
        scroll.scrollToTop();
    }, []);

    useEffect(() => {
        const { ongoing } = completion;

        if (!ongoing.eligibleForInsurance || !feature.salgspantShowCompletionInsurance) {
            action.openStep(4);
        }
    }, [action, completion, feature.salgspantShowCompletionInsurance, insuranceConfirmed]);

    const {
        loanDetailsInsuranceTermsPre,
        loanDetailsInsuranceTermsText1,
        loanDetailsInsuranceTermsText2,
        loanDetailsInsuranceTermsLink1,
        loanDetailsInsuranceTermsLink2,
    } = text;

    const preViewMonthlyCost = loanInfo && loanInfo.monthlyCost ? loanInfo.monthlyCost : 1;
    const insuranceValues = [{ value: JA }, { value: NEI }];
    const insurancePrice = text.loanDetailsInsurancePriceRate
        ? (preViewMonthlyCost * Number(text.loanDetailsInsurancePriceRate)) / 100
        : null;

    return (
        <>
            <div className="insurance-form">
                <div className="loan-details__insurance">
                    <h2 className="loan-application-form-title">{text.loanDetailsInsuranceTitle}</h2>

                    {text.loanDetailsInsuranceDescription && <div>{text.loanDetailsInsuranceDescription}</div>}

                    <div className="loan-application-input">
                        <ButtonToggles
                            label={text.loanDetailsInsurance}
                            onChange={(e) => {
                                fieldUpdateInsurance('orderInsurance', String(e === JA));
                            }}
                            selectedOption={`${ongoing.orderInsurance ? JA : NEI}`}
                            options={insuranceValues}
                        />
                    </div>

                    {ongoing.orderInsurance && (
                        <>
                            {text.loanDetailsInsurancePriceP1 && text.loanDetailsInsurancePriceP2 && insurancePrice && (
                                <div className="summary__insurance-price">
                                    <br />
                                    <br />
                                    {text.loanDetailsInsurancePriceP1}
                                    <br />
                                    {text.loanDetailsInsurancePriceP2}{' '}
                                    <strong>kr {ValutaFilter(insurancePrice, ' ', 0)},-</strong>
                                </div>
                            )}

                            <InfoBox type="info">
                                <>
                                    {text.loanDetailsInsuranceInfoBoxP1 && <p>{text.loanDetailsInsuranceInfoBoxP1}</p>}
                                    <ul>
                                        {text.loanDetailsInsuranceInfoBoxB1 && (
                                            <li>{text.loanDetailsInsuranceInfoBoxB1}</li>
                                        )}
                                        {text.loanDetailsInsuranceInfoBoxB2 && (
                                            <li>{text.loanDetailsInsuranceInfoBoxB2}</li>
                                        )}
                                        {text.loanDetailsInsuranceInfoBoxB3 && (
                                            <li>{text.loanDetailsInsuranceInfoBoxB3}</li>
                                        )}
                                    </ul>
                                </>
                            </InfoBox>

                            {loanDetailsInsuranceTermsPre &&
                                (loanDetailsInsuranceTermsText1 || loanDetailsInsuranceTermsText2) && (
                                    <div className="summary__insurance-terms">
                                        <div>{loanDetailsInsuranceTermsPre}</div>
                                        {loanDetailsInsuranceTermsText1 && (
                                            <Link
                                                href={loanDetailsInsuranceTermsLink1}
                                                title={loanDetailsInsuranceTermsText1}
                                                rel="noopener noreferrer"
                                                target="_blank"
                                            >
                                                {loanDetailsInsuranceTermsText1}
                                            </Link>
                                        )}
                                        {loanDetailsInsuranceTermsText2 && (
                                            <div>
                                                <Link
                                                    href={loanDetailsInsuranceTermsLink2}
                                                    title={loanDetailsInsuranceTermsText2}
                                                    rel="noopener noreferrer"
                                                    target="_blank"
                                                >
                                                    {loanDetailsInsuranceTermsText2}
                                                </Link>
                                            </div>
                                        )}
                                        <br />
                                        <br />
                                    </div>
                                )}
                            <div>
                                <Checkbox
                                    onChange={(e) => {
                                        setInsuranceConfirmed(e.target.checked);
                                    }}
                                    checked={insuranceConfirmed}
                                    label={text.loanDetailsInsuranceConfirm}
                                />
                            </div>
                            {hasErrors && (
                                <span className="sgw-label error sgw-input__error-label">
                                    <span>{text.loanDetailsInsuranceConfirmErrorText}</span>
                                </span>
                            )}
                        </>
                    )}
                    <div className="step-buttons">
                        <LinkButton
                            className="previous-button"
                            icon={IconTypes.LITEN_PIL_VENSTRE}
                            onClick={() => {
                                prevStep();
                            }}
                        >
                            {PREV_BUTTON}
                        </LinkButton>
                        <StandardButton
                            onClick={() => {
                                trackEvent(TrackingStep.CompletionInsurance);
                                if ((ongoing.orderInsurance && insuranceConfirmed) || !ongoing.orderInsurance) {
                                    action.openStep(4);
                                } else {
                                    setHasErrors(true);
                                }
                            }}
                        >
                            {NEXT_BUTTON}
                        </StandardButton>
                    </div>
                </div>
            </div>
        </>
    );
};

export default LoanCompletionInsurance;
